<script setup lang="ts">
import { computed, ComputedRef, onMounted, onUnmounted, ref, Ref, watch } from 'vue'
import { DateTime } from 'luxon'
import DateTimePicker from '@/components/DateTimePicker.vue'
import DurationPicker from '@/components/DurationPicker.vue'
import { quantizeTimeResolution } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { humanReadableTimestamp } from '@/composables/datetime'
import { TimeSeriesChartBuilder } from '@/composables/charts/influx-charts/chart-builders/TimeSeriesChartBuilder'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify/framework'
const { t } = useI18n()

const props = defineProps<{
  chartBuilder: TimeSeriesChartBuilder
}>()

const start: Ref<DateTime> = ref(DateTime.fromMillis(props.chartBuilder.timeSetup.getComputedStart()))
const end: Ref<DateTime> = ref(DateTime.fromMillis(props.chartBuilder.timeSetup.getComputedEnd()))
const timeResolution: Ref<number> = ref(props.chartBuilder.timeSetup.getComputedTimeResolution())
const autoPickTimeResolution: Ref<boolean> = ref(!props.chartBuilder.timeSetup.hasDefinedTimeResolution())
const synchronized: Ref<boolean> = ref(props.chartBuilder.timeSetup.isSynchronized())
const canBeSynchronized: ComputedRef<boolean> = computed(() => !!props.chartBuilder.timeSetup.synchronizationKey)
const hasChanged: ComputedRef<boolean> = computed(() => {
  if (start.value.toMillis() !== props.chartBuilder.timeSetup.getComputedStart()) {
    return true
  }
  if (end.value.toMillis() !== props.chartBuilder.timeSetup.getComputedEnd()) {
    return true
  }
  if (timeResolution.value !== props.chartBuilder.timeSetup.getComputedTimeResolution()) {
    return true
  }
  if (autoPickTimeResolution.value !== !props.chartBuilder.timeSetup.hasDefinedTimeResolution()) {
    return true
  }
  if (synchronized.value !== props.chartBuilder.timeSetup.isSynchronized()) {
    return true
  }
  return false
})
const isTimeResolutionFixed: ComputedRef<boolean> = computed(() => {
  return props.chartBuilder.timeSetup.fixedTimeResolution !== null
})
const showQuickTimeRangePicker: Ref<boolean> = ref(false)
const menuOpen: Ref<boolean> = ref(false)
const availableTimeRanges: { text: string; value: number }[] = [
  { text: t('charts.relative_durations.minutes', { count: 10 }), value: 1000 * 60 * 10 },
  { text: t('charts.relative_durations.minutes', { count: 30 }), value: 1000 * 60 * 30 },
  { text: t('charts.relative_durations.hours', 1), value: 1000 * 60 * 60 },
  { text: t('charts.relative_durations.hours', { count: 2 }), value: 1000 * 60 * 60 * 2 },
  { text: t('charts.relative_durations.hours', { count: 6 }), value: 1000 * 60 * 60 * 6 },
  { text: t('charts.relative_durations.hours', { count: 12 }), value: 1000 * 60 * 60 * 12 },
  { text: t('charts.relative_durations.days', 1), value: 1000 * 60 * 60 * 24 },
  { text: t('charts.relative_durations.days', { count: 2 }), value: 1000 * 60 * 60 * 24 * 2 },
  { text: t('charts.relative_durations.days', { count: 3 }), value: 1000 * 60 * 60 * 24 * 3 },
  { text: t('charts.relative_durations.days', { count: 4 }), value: 1000 * 60 * 60 * 24 * 4 },
  { text: t('charts.relative_durations.weeks', { count: 1 }), value: 1000 * 60 * 60 * 24 * 7 },
  { text: t('charts.relative_durations.weeks', { count: 4 }), value: 1000 * 60 * 60 * 24 * 7 * 4 },
  { text: t('charts.relative_durations.weeks', { count: 12 }), value: 1000 * 60 * 60 * 24 * 7 * 12 },
]

function updateRelativeTimeRange(event: Event, relativeStart: number) {
  event.stopPropagation()
  start.value = DateTime.now().minus({ millisecond: relativeStart })
  end.value = DateTime.now()
  showQuickTimeRangePicker.value = false
}
const humanReadableTimeSetup: ComputedRef<string> = computed(() => {
  return `${humanReadableTimestamp(start.value)} - ${humanReadableTimestamp(end.value)}`
})

watch(autoPickTimeResolution, value => {
  if (value) {
    timeResolution.value = quantizeTimeResolution(start.value.toMillis(), end.value.toMillis())
  }
})

watch([start, end], () => {
  if (autoPickTimeResolution.value) {
    timeResolution.value = quantizeTimeResolution(start.value.toMillis(), end.value.toMillis())
  }
})
function confirmTimeSetup() {
  props.chartBuilder.timeSetup.updateSetup(
    start.value.toMillis(),
    end.value.toMillis(),
    autoPickTimeResolution.value ? null : timeResolution.value,
    synchronized.value && canBeSynchronized.value
  )
  menuOpen.value = false
}

function reset() {
  start.value = DateTime.fromMillis(props.chartBuilder.timeSetup.getComputedStart())
  end.value = DateTime.fromMillis(props.chartBuilder.timeSetup.getComputedEnd())
  timeResolution.value = props.chartBuilder.timeSetup.getComputedTimeResolution()
  autoPickTimeResolution.value = !props.chartBuilder.timeSetup.hasDefinedTimeResolution()
  synchronized.value = props.chartBuilder.timeSetup.isSynchronized()
}

onMounted(() => {
  props.chartBuilder.timeSetup.attachUpdateListener(reset)
})

onUnmounted(() => {
  props.chartBuilder.timeSetup.detachUpdateListener(reset)
})
const menuActivator = ref()
const { mobile } = useDisplay()
</script>

<template>
  <v-btn
    v-if="!mobile"
    ref="menuActivator"
    class="my-2"
    rounded="lg"
    append-icon="mdi-menu"
    prepend-icon="mdi-clock-outline"
  >
    <v-avatar
      v-if="canBeSynchronized"
      size="x-small"
      :icon="synchronized ? 'mdi-link' : 'mdi-link-off'"
      :color="synchronized ? 'success' : 'warning'"
      variant="tonal"
      class="mx-1"
    ></v-avatar>
    {{ humanReadableTimeSetup }}
  </v-btn>
  <v-btn
    v-else
    ref="menuActivator"
    class="my-2"
    rounded="pill"
    >{{ $t('charts.time_setup_picker.title') }}</v-btn
  >

  <v-menu
    v-model="menuOpen"
    :activator="menuActivator"
    :scrim="true"
    :close-on-content-click="false"
  >
    <v-layout-card>
      <v-card-title>{{ $t('charts.time_setup_picker.title') }}</v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            :cols="12"
            :sm="6"
          >
            <DateTimePicker
              v-model="start"
              :label="$t('general_interface.date_time_picker.start')"
              :max="end"
            ></DateTimePicker>
          </v-col>
          <v-col
            :cols="12"
            :sm="6"
          >
            <DateTimePicker
              v-model="end"
              :label="$t('general_interface.date_time_picker.end')"
              :min="start"
            ></DateTimePicker>
          </v-col>

          <v-col :cols="12">
            <v-btn
              variant="outlined"
              class="rounded-pill"
              :block="true"
              size="small"
            >
              {{ $t('charts.quick_time_ranges') }}
              <v-menu
                v-model="showQuickTimeRangePicker"
                activator="parent"
              >
                <v-list>
                  <v-list-item
                    v-for="availableTimeRange in availableTimeRanges"
                    :key="availableTimeRange.value"
                    :title="availableTimeRange.text"
                    @click="updateRelativeTimeRange($event, availableTimeRange.value)"
                  />
                </v-list>
              </v-menu>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider></v-divider>
        <v-card-title>{{ $t('charts.time_resolution') }}</v-card-title>
        <v-row align="center">
          <v-col cols="auto">
            <v-switch
              v-if="!isTimeResolutionFixed"
              v-model="autoPickTimeResolution"
              :hide-details="true"
              :label="$t('charts.time_setup_picker.auto_pick_time_resolution')"
            ></v-switch>
          </v-col>
          <v-col cols="auto">
            <DurationPicker
              v-model="timeResolution"
              :disabled="autoPickTimeResolution || isTimeResolutionFixed"
              :label="$t('charts.time_resolution')"
            ></DurationPicker>
          </v-col>
        </v-row>
        <v-divider v-if="canBeSynchronized"></v-divider>
        <v-switch
          v-if="canBeSynchronized"
          v-model="synchronized"
          :label="$t('charts.time_setup_picker.link_time_setup')"
        ></v-switch>
      </v-card-text>
      <v-card-actions>
        <v-row
          v-if="hasChanged"
          align="center"
          justify="end"
        >
          <v-col cols="auto">
            <v-btn
              color="error"
              rounded="pill"
              variant="outlined"
              @click="reset"
              >{{ $t('general_interface.buttons.reset') }}</v-btn
            >
          </v-col>

          <v-col cols="auto">
            <v-btn
              color="primary"
              rounded="pill"
              variant="elevated"
              @click="confirmTimeSetup"
              >{{ $t('general_interface.buttons.confirm') }}</v-btn
            >
          </v-col>
        </v-row>
      </v-card-actions>
    </v-layout-card>
  </v-menu>
</template>

<style scoped></style>
